import { Alert, Button, Tag, Table } from "antd";
import { FinanceEntryType } from "../typeDefs";
import { requiresGstDetails } from "./taxConfig";

const money = (value: number) => `$${(value || 0).toFixed(2)}`;

const allowedCategories = ["Revenue", "Expenses", "Assets", "Liabilities", "Equity", "Non-Business"];

type IntegrityIssue = {
  key: string;
  severity: "error" | "warning" | "review";
  entryId?: string;
  date?: number;
  vendor?: string;
  amount?: number;
  issue: string;
  recommendation: string;
};

export const DataIntegrityCheck = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const entries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]);

  const issues: IntegrityIssue[] = [];
  const bankIds = new Map<string, FinanceEntryType[]>();
  const dateAmountMatches = new Map<string, FinanceEntryType[]>();

  entries.forEach((entry) => {
    if (entry.bankTransactionId) {
      const existing = bankIds.get(entry.bankTransactionId) || [];
      bankIds.set(entry.bankTransactionId, [...existing, entry]);
    }

    const dateAmountKey = `${entry.date}_${Number(entry.amount || 0).toFixed(2)}`;
    const existingDateAmount = dateAmountMatches.get(dateAmountKey) || [];
    dateAmountMatches.set(dateAmountKey, [...existingDateAmount, entry]);

    if (!entry.id) {
      issues.push({
        key: `missing-id-${entry.vendor}-${entry.date}`,
        severity: "error",
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Entry is missing an ID.",
        recommendation: "Check Firestore document and repair the finance entry ID.",
      });
    }

    if (!Number.isFinite(entry.date) || entry.date < new Date("2000-01-01").getTime()) {
      issues.push({
        key: `bad-date-${entry.id}`,
        severity: "error",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Invalid or suspicious date.",
        recommendation: "Edit the finance entry and set the correct transaction date.",
      });
    }

    if (!Number.isFinite(entry.amount) || entry.amount < 0) {
      issues.push({
        key: `bad-amount-${entry.id}`,
        severity: "error",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Invalid amount. Amounts should be positive claim/income values.",
        recommendation: "Edit the finance entry amount. Direction is represented by category, not a negative amount.",
      });
    }

    if (!entry.vendor || entry.vendor.trim().length < 2) {
      issues.push({
        key: `missing-vendor-${entry.id}`,
        severity: "warning",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Missing or very short vendor name.",
        recommendation: "Add a clear vendor/payee name for accountant review and future auto-reconciliation.",
      });
    }

    if (!entry.description || entry.description.trim().length < 2) {
      issues.push({
        key: `missing-description-${entry.id}`,
        severity: "warning",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Missing description.",
        recommendation: "Add a short business reason or transaction description.",
      });
    }

    if (!allowedCategories.includes(entry.category)) {
      issues.push({
        key: `bad-category-${entry.id}`,
        severity: "error",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: `Unknown category: ${entry.category}`,
        recommendation: "Change category to Revenue, Expenses, Assets, Liabilities, Equity, or Non-Business.",
      });
    }

    if (!entry.subCategory) {
      issues.push({
        key: `missing-subcategory-${entry.id}`,
        severity: "warning",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Missing subcategory.",
        recommendation: "Set an accounting subcategory for reporting and tax review.",
      });
    }

    if (requiresGstDetails(entry)) {
      if (typeof entry.gstIncluded !== "boolean" || typeof entry.gstRegistered !== "boolean") {
        issues.push({
          key: `missing-gst-${entry.id}`,
          severity: "error",
          entryId: entry.id,
          date: entry.date,
          vendor: entry.vendor,
          amount: entry.amount,
          issue: "Missing GST included/registered information.",
          recommendation: "Open GST Report and resolve GST details for this entry.",
        });
      }
    }

    if ((entry.category === "Expenses" || entry.category === "Assets") && !entry.receipt) {
      issues.push({
        key: `missing-receipt-${entry.id}`,
        severity: "review",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Expense/asset entry has no receipt attached.",
        recommendation: "Attach receipt if available or keep external evidence for accountant.",
      });
    }

    if (entry.isHomeOfficeExpense) {
      if (!entry.businessUseRate || entry.businessUseRate <= 0 || entry.businessUseRate > 100) {
        issues.push({
          key: `bad-home-office-rate-${entry.id}`,
          severity: "error",
          entryId: entry.id,
          date: entry.date,
          vendor: entry.vendor,
          amount: entry.amount,
          issue: "Home-office expense has invalid business-use percentage.",
          recommendation: "Set business-use percentage between 1 and 100.",
        });
      }

      if (!entry.originalAmount || entry.originalAmount < entry.amount) {
        issues.push({
          key: `bad-home-office-original-${entry.id}`,
          severity: "warning",
          entryId: entry.id,
          date: entry.date,
          vendor: entry.vendor,
          amount: entry.amount,
          issue: "Home-office expense is missing original full amount or original amount is lower than claim amount.",
          recommendation: "Store the original bill amount and claim amount separately.",
        });
      }
    }

    if (
      (entry.subCategory === "Shareholder Salary" || entry.subCategory === "Shareholder Drawings") &&
      (entry.gstIncluded || entry.gstRegistered)
    ) {
      issues.push({
        key: `shareholder-gst-${entry.id}`,
        severity: "error",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Shareholder salary/drawings entry appears to have GST enabled.",
        recommendation: "Set GST included and GST registered to No unless accountant confirms otherwise.",
      });
    }

    if (entry.category === "Assets" && !entry.depreciationMethod) {
      issues.push({
        key: `asset-depreciation-${entry.id}`,
        severity: "review",
        entryId: entry.id,
        date: entry.date,
        vendor: entry.vendor,
        amount: entry.amount,
        issue: "Asset has no depreciation method set.",
        recommendation: "Open Asset Register or edit entry and confirm depreciation treatment with accountant.",
      });
    }
  });

  bankIds.forEach((matches, bankTransactionId) => {
    if (matches.length > 1) {
      matches.forEach((entry) => {
        issues.push({
          key: `duplicate-bank-${bankTransactionId}-${entry.id}`,
          severity: "error",
          entryId: entry.id,
          date: entry.date,
          vendor: entry.vendor,
          amount: entry.amount,
          issue: "Duplicate bank transaction ID found.",
          recommendation: "Review duplicates and delete or merge accidental duplicate entries.",
        });
      });
    }
  });

  dateAmountMatches.forEach((matches) => {
    if (matches.length > 1) {
      matches.forEach((entry) => {
        issues.push({
          key: `same-date-amount-${entry.id}`,
          severity: "review",
          entryId: entry.id,
          date: entry.date,
          vendor: entry.vendor,
          amount: entry.amount,
          issue: "Another entry has the same date and amount.",
          recommendation: "Confirm this is not a duplicate import or duplicate manual entry.",
        });
      });
    }
  });

  const errorCount = issues.filter((issue) => issue.severity === "error").length;
  const warningCount = issues.filter((issue) => issue.severity === "warning").length;
  const reviewCount = issues.filter((issue) => issue.severity === "review").length;

  const exportIssues = () => {
    const headers = ["Severity", "Date", "Vendor", "Amount", "Issue", "Recommendation", "Entry ID"];
    const csv = [
      headers.join(","),
      ...issues.map((issue) =>
        [
          issue.severity,
          issue.date ? new Date(issue.date).toLocaleDateString("en-GB") : "",
          issue.vendor || "",
          issue.amount || "",
          issue.issue,
          issue.recommendation,
          issue.entryId || "",
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "finance-data-integrity-issues.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type={errorCount ? "error" : warningCount || reviewCount ? "warning" : "success"}
        showIcon
        message={
          errorCount
            ? "Data integrity errors found"
            : warningCount || reviewCount
              ? "Data integrity review recommended"
              : "Selected finance data passed integrity checks"
        }
        description={`${entries.length} entries checked. ${errorCount} errors, ${warningCount} warnings, ${reviewCount} review items.`}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Tag color="red">Errors: {errorCount}</Tag>
        <Tag color="orange">Warnings: {warningCount}</Tag>
        <Tag color="blue">Review: {reviewCount}</Tag>
        <Button disabled={!issues.length} onClick={exportIssues}>Export issues CSV</Button>
      </div>

      <Table
        title={() => "Integrity issues for selected date range"}
        size="small"
        pagination={{ pageSize: 12 }}
        dataSource={issues}
        columns={[
          {
            title: "Severity",
            dataIndex: "severity",
            render: (value) => (
              <Tag color={value === "error" ? "red" : value === "warning" ? "orange" : "blue"}>
                {value.toUpperCase()}
              </Tag>
            ),
          },
          { title: "Date", dataIndex: "date", render: (value) => value ? new Date(value).toLocaleDateString("en-GB") : "-" },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Amount", dataIndex: "amount", render: (value) => typeof value === "number" ? money(value) : "-" },
          { title: "Issue", dataIndex: "issue" },
          { title: "Recommended action", dataIndex: "recommendation" },
          { title: "Entry ID", dataIndex: "entryId" },
        ]}
      />
    </div>
  );
};
